<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

defineProps({
  installments: { type: Array, default: () => [] },
  menuItems: { type: Array, default: () => [] },
  money: { type: Function, required: true },
});

const emit = defineEmits(['edit', 'preview']);
const { t } = useI18n();
const openMenuId = ref(null);
const toggleMenu = (event, installmentId) => {
  event.stopPropagation();
  openMenuId.value = openMenuId.value === installmentId ? null : installmentId;
};
const handleMenuAction = (installment, item) => {
  openMenuId.value = null;
  if (item.action === 'preview') emit('preview', installment);
  else emit('edit', installment);
};
const formatSentAt = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(date);
};
const deliveryLabel = installment =>
  installment.enviada
    ? t('REAL_ESTATE_COMMISSION_PLAN.STATUS.SENT_AT', {
        date: formatSentAt(installment.ultima_data_envio),
      })
    : t('REAL_ESTATE_COMMISSION_PLAN.STATUS.READY');
</script>

<template>
  <LaisCard
    class="mt-5"
    :title="t('REAL_ESTATE_COMMISSION_PLAN.SUMMARY.SHEETS')"
    prefix-icon="i-lucide-files"
  >
    <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <article
        v-for="installment in installments"
        :key="installment.id"
        class="cursor-pointer rounded-lais border border-lais-border-soft bg-lais-bg-soft p-4"
        role="button"
        tabindex="0"
        @click="emit('edit', installment)"
        @keydown.enter="emit('edit', installment)"
      >
        <div class="flex items-start justify-between gap-2">
          <p class="font-semibold text-lais-text">
            {{
              t('REAL_ESTATE_COMMISSION_PLAN.SUMMARY.SHEET', {
                number: installment.numero_parcela,
              })
            }}
          </p>
          <div class="flex items-center gap-2">
            <span
              class="rounded-full px-2 py-1 text-xs font-bold"
              :class="
                installment.enviada
                  ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-orange-100 text-orange-800'
              "
            >
              {{
                installment.enviada
                  ? t('REAL_ESTATE_COMMISSION_PLAN.STATUS.SENT')
                  : t('REAL_ESTATE_COMMISSION_PLAN.STATUS.PENDING')
              }}
            </span>
            <div class="relative">
              <button
                type="button"
                class="flex size-8 items-center justify-center rounded-full text-lais-text-muted hover:bg-white hover:text-lais-text"
                data-testid="installment-menu"
                @click="toggleMenu($event, installment.id)"
              >
                <span class="i-lucide-ellipsis size-4" />
              </button>
              <DropdownMenu
                v-if="openMenuId === installment.id"
                class="right-0 top-9"
                :menu-items="menuItems"
                @click.stop
                @action="handleMenuAction(installment, $event)"
              />
            </div>
          </div>
        </div>
        <div class="mt-4 flex items-center gap-3">
          <span
            class="size-12 shrink-0 text-lais-primary"
            :class="
              installment.enviada
                ? 'i-lucide-circle-check-big'
                : 'i-lucide-circle-dollar-sign'
            "
          />
          <div class="min-w-0">
            <p
              data-testid="installment-delivery"
              class="text-xs font-bold text-lais-text-muted"
            >
              {{ deliveryLabel(installment) }}
            </p>
            <p class="mt-1 text-lg font-semibold text-lais-primary">
              {{ money(installment.valor_parcela) }}
            </p>
            <p class="mt-1 text-xs text-lais-text-muted">
              {{
                installment.periodo_pagamento ||
                t('REAL_ESTATE_COMMISSION_PLAN.COMMON.NOT_INFORMED')
              }}
            </p>
          </div>
        </div>
      </article>
    </div>
  </LaisCard>
</template>
